import {
	Component,
	Input,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	ElementRef,
	ViewChild,
	NgZone,
	OnChanges,
	SimpleChanges,
	Output,
	EventEmitter,
	AfterViewInit,
	Renderer2,
	OnDestroy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgIf, NgForOf, NgClass, NgStyle } from '@angular/common';
import { Subscription } from 'rxjs';

export interface Step {
	title: string;
	content: string;
}

export interface TOCItem {
	title: string;
	id: string;
	level: number;
	stepIndex: number;
}

@Component({
	selector: 'app-freshdesk-solution-viewer',
	standalone: true,
	imports: [NgIf, NgClass, NgForOf, NgStyle],
	templateUrl: './freshdesk-solution-viewer.component.html',
	styleUrls: ['./freshdesk-solution-viewer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreshdeskSolutionViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
	@Input() htmlContent: string = '';
	@Input() title: string = '';
	@Input() selectedArticleId: string | number | null = null; // new input
	@ViewChild('contentContainer') contentContainer!: ElementRef<HTMLDivElement>;
	@Output() articleRequested = new EventEmitter<string>();

	public steps: Step[] = [];
	public currentStepIndex = 0;
	public tableOfContents: TOCItem[] = [];

	constructor(private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef, private ngZone: NgZone, private renderer: Renderer2, private el: ElementRef) { }

	public tocCollapsed = false;

	toggleToc(): void {
		this.tocCollapsed = !this.tocCollapsed;
		this.cdr.markForCheck();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['htmlContent'] && this.htmlContent) {
			this.steps = this.parseSteps(this.htmlContent);
			if (!this.steps.length) this.steps = [{ title: 'Content', content: this.htmlContent }];

			// Restore last step using articleId
			if (this.selectedArticleId !== null) {
				const key = this.encodeKey(`articleStep-${this.selectedArticleId}`);
				const lastStep = localStorage.getItem(key);
				this.currentStepIndex = lastStep ? Number(lastStep) : 0;
			} else {
				this.currentStepIndex = 0;
			}

			this.buildTableOfContents();
			this.cdr.markForCheck();
		}
	}

	private subs = new Subscription();
	private removeClickListener?: () => void;

	public lightboxImage: string | null = null;

	// Listen for clicks on images
	ngAfterViewInit(): void {

		this.subs.add(
			this.ngZone.onStable.asObservable().subscribe(() => {
				this.applyImageSkeletons();
			})
		);

		this.removeClickListener = this.renderer.listen(
			this.contentContainer.nativeElement,
			'click',
			(event: Event) => { /* existing handler */ }
		);

		document.documentElement.style.setProperty('--steps', this.steps.length.toString());

		this.contentContainer.nativeElement.addEventListener('click', (event: Event) => {
			const target = event.target as HTMLElement;

			// Look for the nearest <img> even if inside a <span>
			const img = target.closest('img');
			if (img) {
				this.openLightbox((img as HTMLImageElement).src);
				return; // stop further processing
			}

			// Optional: handle internal links
			const link = target.closest('a');
			if (link) {
				const href = (link as HTMLAnchorElement).href;
				const match = href.match(/articles\/(\d+)/);
				if (match && match[1]) {
					event.preventDefault();
					this.articleRequested.emit(match[1]);
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	private applyImageSkeletons() {
		if (!this.contentContainer) return;

		const images: NodeListOf<HTMLImageElement> = this.contentContainer.nativeElement.querySelectorAll('img');

		images.forEach(img => {
			if (img.dataset.skeletonApplied) return;
			img.dataset.skeletonApplied = 'true';

			// Hide image initially
			this.renderer.setStyle(img, 'visibility', 'hidden');

			// Create skeleton placeholder
			const skeleton = this.renderer.createElement('div');
			this.renderer.addClass(skeleton, 'skeleton');

			this.renderer.setStyle(skeleton, 'width', '100%');
			this.renderer.setStyle(skeleton, 'margin', '10px 0');
			this.renderer.setStyle(skeleton, 'height', '200px'); // temporary

			this.renderer.insertBefore(img.parentNode, skeleton, img);

			img.addEventListener('load', () => {

				if (!img.naturalWidth) {
					this.renderer.setStyle(img, 'visibility', 'visible');
					skeleton.remove();
					return;
				}

				const aspectRatio = img.naturalHeight / img.naturalWidth;
				const width = img.clientWidth || img.naturalWidth;
				const height = width * aspectRatio;
				this.renderer.setStyle(img, 'height', `${height}px`);
				this.renderer.setStyle(img, 'visibility', 'visible');

				// Fade in image smoothly
				this.renderer.setStyle(img, 'opacity', '0');
				this.renderer.setStyle(img, 'transition', 'opacity 0.3s ease-in');
				requestAnimationFrame(() => {
					this.renderer.setStyle(img, 'opacity', '1');
				});

				skeleton.remove();
				this.renderer.addClass(img, 'loaded');
			});

			if (img.complete) {
				img.dispatchEvent(new Event('load'));
			}
		});
	}

	/** Open image modal */
	openLightbox(src: string) {
		this.lightboxImage = src;
		this.cdr.markForCheck(); // tell Angular to re-render
	}

	/** Close image modal */
	closeLightbox() {
		this.lightboxImage = null;
		this.cdr.markForCheck(); // tell Angular to re-render
	}

	/** Pagination */
	nextStep() {
		if (this.currentStepIndex < this.steps.length - 1) {
			this.currentStepIndex++;
			this.saveStep();
		}
	}

	previousStep() {
		if (this.currentStepIndex > 0) {
			this.currentStepIndex--;
			this.saveStep();
		}
	}

	get currentStep(): Step {
		return this.steps[this.currentStepIndex];
	}

	get currentStepContent(): SafeHtml {
		if (!this.currentStep?.content) return '';

		let html = this.currentStep.content
			.replace(/\\"/g, '"')
			.replace(/&quot;/g, '"')
			.replace(/&amp;/g, '&')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&#39;/g, "'");

		// Enhance fd-callout--note boxes
		html = html.replace(
			/<pre class="fd-callout fd-callout--note"([^>]*)>/gi,
			'<pre class="fd-callout fd-callout--note"$1 style="border-left: 4px solid #e25041; padding: 10px 15px; background-color: #fff4f2; max-width: 100%; white-space: pre-wrap; word-wrap: break-word; box-sizing: border-box;">'
		);

		// Enhance fd-callout--note boxes
		html = html.replace(
			/<pre class="fd-callout fd-callout--note"([^>]*)>/gi,
			(_match, attrs) => {
				// Preserve any existing style
				const style = attrs.match(/style="([^"]*)"/)?.[1] || '';
				const newStyle = `${style}; border-left: 4px solid #e25041; padding: 10px 15px; background-color: #fff4f2; max-width: 100%; white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; overflow-x: auto; box-sizing: border-box;`.trim();
				// Replace existing style or add new
				if (/style=/.test(attrs)) {
					return `<pre class="fd-callout fd-callout--note"${attrs.replace(/style="([^"]*)"/, `style="${newStyle}"`)}>`;
				} else {
					return `<pre class="fd-callout fd-callout--note"${attrs} style="${newStyle}">`;
				}
			}
		);

		// Enhance images: cursor zoom-in and ensure max-width
		html = html.replace(
			/<img([^>]*?)\ssrc=["']([^"'>]+?)["']([^>]*)>/gi,
			(_match, before, url, after) => {
				if (url.includes('freshdesk.com') || url.startsWith('http')) {
					// Check if the original <img> has a style attribute
					const styleMatch = (_match.match(/style="([^"]*)"/) || [])[1] || '';
					const newStyle = `${styleMatch}; cursor: zoom-in; max-width:100%; height:auto; display:block; margin:10px auto;`.trim();

					// Replace existing style or add it if missing
					if (/style=/.test(_match)) {
						return _match.replace(/style="([^"]*)"/, `style="${newStyle}"`);
					} else {
						return `<img${before} src="${url}"${after} style="${newStyle}" loading="lazy">`;
					}
				}
				return _match;
			}
		);

		return this.sanitizer.bypassSecurityTrustHtml(html);
	}

	get progressPercent() {
		if (this.steps.length <= 1) return 100; // or 0, depending on desired behavior
		return (this.currentStepIndex / (this.steps.length - 1)) * 100;
	}

	scrollTo(id: string, event?: Event) {
		if (event) event.preventDefault();

		const tocItem = this.tableOfContents.find(item => item.id === id);
		if (!tocItem) return;

		this.currentStepIndex = tocItem.stepIndex;
		this.saveStep();
		this.cdr.markForCheck();

		this.ngZone.onStable.asObservable().pipe().subscribe(() => {
			const safeSelector = `#${CSS.escape(id)}`;
			const el = this.contentContainer?.nativeElement.querySelector(safeSelector);
			if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	}

	isActive(item: TOCItem): boolean {
		return item.stepIndex === this.currentStepIndex;
	}

	private normalizeId(text: string): string {
		return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
	}

	private buildTableOfContents() {
		this.tableOfContents = [];
		const idCounts: Record<string, number> = {}; // Track duplicates

		this.steps.forEach((step, stepIndex) => {
			const div = document.createElement('div');
			div.innerHTML = step.content;
			const headings = div.querySelectorAll('h1, h2, h3');

			headings.forEach(h => {

				// Always ignore incoming Freshdesk IDs
				h.removeAttribute('id');

				// Generate safe ID
				let id = this.normalizeId(h.textContent || '');

				// Ensure ID is not empty
				if (!id) id = `section-${stepIndex}-${this.tableOfContents.length}`;

				// Ensure uniqueness
				if (idCounts[id] != null) {
					idCounts[id]++;
					id = `${id}-${idCounts[id]}`;
				} else {
					idCounts[id] = 0;
				}

				h.id = id;

				this.tableOfContents.push({
					title: h.textContent?.trim() || '',
					id,
					level: parseInt(h.tagName.replace('H', ''), 10),
					stepIndex,
				});
			});
		});
	}

	private parseSteps(html: string): Step[] {
		if (!html) return [];

		// decode a few HTML entities you may still want
		const cleanHtml = html
			.replace(/\\"/g, '"')
			.replace(/\\\\/g, '\\')
			.replace(/&quot;/g, '"')
			.replace(/&amp;/g, '&')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&#39;/g, "'");

		const parser = new DOMParser();
		const doc = parser.parseFromString(cleanHtml, 'text/html');

		const steps: Step[] = [];
		const headings = Array.from(doc.querySelectorAll('h1, h2, h3'));

		if (headings.length === 0) {
			// no headings — return whole content as single step
			return [{ title: 'Content', content: cleanHtml }];
		}

		headings.forEach((heading, idx) => {
			const title = (heading.textContent || '').trim() || `Step ${idx + 1}`;

			// gather nodes until the next heading element
			const contentNodes: Node[] = [];
			let node: Node | null = heading.nextSibling;
			while (node) {
				if (node.nodeType === Node.ELEMENT_NODE) {
					const el = node as Element;
					if (/^H[1-3]$/.test(el.tagName)) break; // stop at next heading
				}
				contentNodes.push(node);
				node = node.nextSibling;
			}

			// helper to normalise visible text for comparisons
			const normalize = (s: string) =>
				s.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

			const titleNorm = normalize(title);

			// filter out nodes that are *only* the duplicated title (possibly wrapped in spans/tags)
			const cleanedNodes = contentNodes.filter(n => {
				const txt = (n.textContent || '').trim();
				if (!txt) {
					// keep nodes that have no text (hr, images, etc.)
					return true;
				}
				const nNorm = normalize(txt);

				// If the node's visible text exactly equals the title -> drop it
				if (nNorm === titleNorm) return false;

				// If the node's visible text is the title with only a tiny extra (like nbsp or punctuation),
				// or it starts with the title and has negligible extra content, drop it.
				if (nNorm.startsWith(titleNorm) && nNorm.length - titleNorm.length <= 5) return false;

				// otherwise keep it
				return true;
			});

			// serialize cleaned nodes back to html (preserve elements and text nodes)
			const serialized = cleanedNodes
				.map(n => (n.nodeType === Node.ELEMENT_NODE ? (n as Element).outerHTML : (n.textContent || '')))
				.join('');

			steps.push({
				title,
				content: heading.outerHTML + serialized
			});
		});

		// filter out Table of Contents and fallback
		const filtered = steps.filter(s => s.title.toLowerCase() !== 'table of contents');
		return filtered.length ? filtered : [{ title: 'Content', content: cleanHtml }];
	}

	/** Persist current step per article */
	private saveStep() {
		if (this.currentStep && this.selectedArticleId !== null) {
			const key = this.encodeKey(`articleStep-${this.selectedArticleId}`);
			localStorage.setItem(key, this.currentStepIndex.toString());
		}
	}

	/** Safe Base64 encoding for Unicode */
	private encodeKey(str: string): string {
		return btoa(unescape(encodeURIComponent(str)));
	}

	private decodeKey(str: string): string {
		return decodeURIComponent(escape(atob(str)));
	}
}
